import { useState } from 'react'
import { ClipboardPaste, Sheet } from 'lucide-react'
import type { ClientDraft, ParsedTable } from './types'
import { parseDelimited, looksLikeOnboardSheet } from './parse'
import { ImportReview } from './ImportReview'

const SAMPLE = `name\tvertical\tplan\tmrr\thealth
Harbor View Dentistry\tDental\tRetainer + Answer\t1200\tgood
Loft & Co Salon\tBeauty\tAnswer ($99/mo)\t99\twatch
Bayside Auto Care\tAutomotive\tRetainer\t850\tgood`

interface PasteIntakeProps {
  onImport: (drafts: ClientDraft[]) => void
  isSubmitting: boolean
  /** Offered when the pasted sheet carries team/task columns this importer would ignore. */
  onSwitchToOnboard?: () => void
}

export function PasteIntake({ onImport, isSubmitting, onSwitchToOnboard }: PasteIntakeProps) {
  const [text, setText] = useState('')
  const [table, setTable] = useState<ParsedTable | null>(null)
  const [error, setError] = useState<string | null>(null)

  const parse = () => {
    setError(null)
    const parsed = parseDelimited(text)
    if (!parsed || parsed.rows.length === 0) {
      setError('Paste at least a header row and one data row (tab, comma, or semicolon separated).')
      return
    }
    setTable(parsed)
  }

  if (table) {
    return (
      <>
        {onSwitchToOnboard && looksLikeOnboardSheet(table.headers) && (
          <div className="intake-banner intake-banner--warn" style={{ marginBottom: 'var(--space-2)' }}>
            <Sheet size={15} />
            <span>
              These rows carry <strong>team member and task columns</strong>, which this importer ignores —
              it only creates the client records.{' '}
              <button type="button" className="btn btn-ghost btn-sm" onClick={onSwitchToOnboard}>
                Use Onboard import instead
              </button>
            </span>
          </div>
        )}
        <ImportReview
          table={table}
          onImport={onImport}
          onReset={() => setTable(null)}
          isSubmitting={isSubmitting}
        />
      </>
    )
  }

  return (
    <div className="intake-paste">
      <div className="intake-field">
        <label htmlFor="intake-paste-area">Paste rows from a spreadsheet or a list</label>
        <textarea
          id="intake-paste-area"
          className="intake-input intake-textarea intake-paste-area"
          rows={8}
          placeholder={
            'Paste tab- or comma-separated rows. Include a header row, e.g.\n\nname, vertical, plan, mrr\nHarbor View Dentistry, Dental, Retainer, 1200'
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
        />
      </div>

      {error && <p className="intake-errors intake-errors--block">{error}</p>}

      <div className="intake-footer">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            setText(SAMPLE)
            setError(null)
          }}
        >
          Use sample
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={parse}
          disabled={!text.trim() || isSubmitting}
        >
          <ClipboardPaste size={14} /> Parse rows
        </button>
      </div>
    </div>
  )
}
